import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/Payroll_AddAdvanceInput.ts';
import '../contracts/Payroll_AddAdvancesBatchInput.ts';
import '../contracts/Payroll_AvailableRecebimentosForAdvancesResponse.ts';
import '../contracts/Payroll_AdvancesResponse.ts';
import '../contracts/Payroll_AvailableRecebimentosForAdvancesResponse.ts';
import '../contracts/Payroll_AddAdvanceResponse';
import '../contracts/Payroll_AddAdvancesBatchResponse';
import '../contracts/Payroll_AdvancesResponse';
import '../contracts/Payroll_AdvancesSummaryResponse';
import '../contracts/Payroll_AvailableRecebimentosForAdvancesResponse';
import '../contracts/Payroll_DeleteAdvanceResponse';

import type { Payroll_AddAdvanceInput } from '../contracts/Payroll_AddAdvanceInput';
import type { Payroll_AddAdvancesBatchInput } from '../contracts/Payroll_AddAdvancesBatchInput';
/**
 * Composable for managing payroll advances
 *
 * Payroll advances track cash recebimentos held by operadores.
 * When an operador collects cash from a customer, they keep the money.
 * These advances are reconciled during payroll period processing.
 *
 * At confirmation:
 * - Each advance creates a separate despesa with status = 'baixado'
 * - Final salary = net_amount - deductions - advances
 *
 * Formula: Advances (baixado) + Final Salary (pendente) = net_amount - deductions
 */
export function usePayrollAdvances() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Get available cash recebimentos that can be selected as advances
   *
   * Returns recebimentos where:
   * - Payment method is CASH
   * - Within period date range
   * - Linked to compromisso with operador who has commission rule
   * - Not already selected as advance
   *
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Array>} Available recebimentos
   */
  const fetchAvailableRecebimentos = async (periodId: number) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollAdvances', 'fetchAvailableRecebimentos', 'Fetching available recebimentos', { periodId });

      const response = await httpService.get(`/payroll/available_recebimentos_for_advances/${periodId}`, {
        responseContract: 'Payroll_AvailableRecebimentosForAdvancesResponse',
      });

      if (response.success) {
        log('usePayrollAdvances', 'fetchAvailableRecebimentos', 'Recebimentos fetched', response.dados);
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Falha ao buscar recebimentos disponíveis');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollAdvances', 'fetchAvailableRecebimentos', 'Error fetching recebimentos', err);
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * List all advances for a payroll period
   *
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Object>} Object with advances, summary_by_user, and total_advances
   */
  const listAdvancesByPeriod = async (periodId: number) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollAdvances', 'listAdvancesByPeriod', 'Fetching advances', { periodId });

      const response = await httpService.get(`/payroll/advances?period_id=${periodId}`, {
        responseContract: 'Payroll_AdvancesResponse',
      });

      if (response.success) {
        log('usePayrollAdvances', 'listAdvancesByPeriod', 'Advances fetched', response.dados);
        return response.dados || { advances: [], summary_by_user: [], total_advances: 0 };
      } else {
        throw new Error(response.error || 'Falha ao buscar adiantamentos');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollAdvances', 'listAdvancesByPeriod', 'Error fetching advances', err);
      return { advances: [], summary_by_user: [], total_advances: 0 };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * List advances for a specific user in a period
   *
   * @param {string} userId - Cognito sub
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Object>} Object with advances and totals
   */
  const listAdvancesByUserAndPeriod = async (userId: any, periodId: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollAdvances', 'listAdvancesByUserAndPeriod', 'Fetching user advances', { userId, periodId });

      const response = await httpService.get(`/payroll/advances?period_id=${periodId}&user_id=${userId}`, {
        responseContract: 'Payroll_AdvancesResponse',
      });

      if (response.success) {
        log('usePayrollAdvances', 'listAdvancesByUserAndPeriod', 'User advances fetched', response.dados);
        return response.dados || { advances: [], summary_by_user: [], total_advances: 0 };
      } else {
        throw new Error(response.error || 'Falha ao buscar adiantamentos do usuário');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollAdvances', 'listAdvancesByUserAndPeriod', 'Error fetching user advances', err);
      return { advances: [], summary_by_user: [], total_advances: 0 };
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Add a single payroll advance
   *
   * @param {Object} advanceData
   * @param {number} advanceData.period_id - Payroll period ID
   * @param {number} advanceData.recebimento_id - Recebimento ID
   * @param {number} advanceData.franchise_id - Franchise ID
   * @param {string} [advanceData.notes] - Optional notes
   * @returns {Promise<Object|null>} Created advance or null
   */
  const addAdvance = async (advanceData: Payroll_AddAdvanceInput) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollAdvances', 'addAdvance', 'Adding advance', advanceData);

      validate('Payroll_AddAdvanceInput', advanceData);
      const response = await httpService.post('/payroll/add_advance', advanceData, {
        responseContract: 'Payroll_AddAdvanceResponse'
      });

      if (response.success) {
        log('usePayrollAdvances', 'addAdvance', 'Advance added successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Falha ao adicionar adiantamento');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollAdvances', 'addAdvance', 'Error adding advance', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Add multiple advances at once (batch)
   *
   * @param {Object} batchData
   * @param {number} batchData.period_id - Payroll period ID
   * @param {Array<number>} batchData.recebimento_ids - Array of recebimento IDs
   * @param {number} batchData.franchise_id - Franchise ID
   * @param {string} [batchData.notes] - Optional notes
   * @returns {Promise<Object|null>} Result with created advances or null
   */
  const addAdvancesBatch = async (batchData: Payroll_AddAdvancesBatchInput) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollAdvances', 'addAdvancesBatch', 'Adding advances batch', batchData);

      validate('Payroll_AddAdvancesBatchInput', batchData);
      const response = await httpService.post('/payroll/add_advances_batch', batchData, {
        responseContract: 'Payroll_AddAdvancesBatchResponse'
      });

      if (response.success) {
        log('usePayrollAdvances', 'addAdvancesBatch', 'Batch added successfully', response.dados);
        return response.dados;
      } else {
        throw new Error(response.error || 'Falha ao adicionar adiantamentos em lote');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollAdvances', 'addAdvancesBatch', 'Error adding batch', err);
      return null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete a payroll advance
   *
   * @param {number} advanceId - Advance ID
   * @param {number} franchiseId - Franchise ID for validation
   * @returns {Promise<boolean>} Success status
   */
  const deleteAdvance = async (advanceId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollAdvances', 'deleteAdvance', 'Deleting advance', { advanceId, franchiseId });

      const response = await httpService.delete(`/payroll/delete_advance/${advanceId}?franchise_id=${franchiseId}`, {
        responseContract: 'Payroll_DeleteAdvanceResponse'
      });

      if (response.success) {
        log('usePayrollAdvances', 'deleteAdvance', 'Advance deleted successfully');
        return true;
      } else {
        throw new Error(response.error || 'Falha ao deletar adiantamento');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollAdvances', 'deleteAdvance', 'Error deleting advance', err);
      return false;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get advances summary by user for a period
   *
   * Used for confirmation step to show totals with user details
   *
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Array>} Summary by user
   */
  const fetchAdvancesSummary = async (periodId: number) => {
    isLoading.value = true;
    error.value = null;

    try {
      log('usePayrollAdvances', 'fetchAdvancesSummary', 'Fetching summary', { periodId });

      const response = await httpService.get(`/payroll/advances_summary/${periodId}`, {
        responseContract: 'Payroll_AdvancesSummaryResponse'
      });

      if (response.success) {
        log('usePayrollAdvances', 'fetchAdvancesSummary', 'Summary fetched', response.dados);
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Falha ao buscar resumo de adiantamentos');
      }
    } catch (err) {
      error.value = err.message;
      log('usePayrollAdvances', 'fetchAdvancesSummary', 'Error fetching summary', err);
      return [];
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get total advances for a specific user in a period
   *
   * @param {string} userId - Cognito sub
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<number>} Total advances amount
   */
  const getTotalByUserAndPeriod = async (userId: any, periodId: any) => {
    try {
      log('usePayrollAdvances', 'getTotalByUserAndPeriod', 'Calculating total', { userId, periodId });

      const data = await listAdvancesByUserAndPeriod(userId, periodId);
      const total = data.total_advances || 0;

      log('usePayrollAdvances', 'getTotalByUserAndPeriod', 'Total calculated', total);
      return total;
    } catch (err) {
      error.value = err.message;
      log('usePayrollAdvances', 'getTotalByUserAndPeriod', 'Error calculating total', err);
      return 0;
    }
  };

  /**
   * Get grouped advances by user for a period
   *
   * @param {number} periodId - Payroll period ID
   * @returns {Promise<Object>} Advances grouped by user_id
   */
  const getGroupedByUser = async (periodId: number) => {
    try {
      log('usePayrollAdvances', 'getGroupedByUser', 'Grouping advances', { periodId });

      const data = await listAdvancesByPeriod(periodId);
      const advances = data.advances || [];

      const grouped: any = {};
      advances.forEach((advance: any) => {
        const userId = advance.user_id;
        if (!grouped[userId]) {
          grouped[userId] = {
            user_id: userId,
            total: 0,
            items: []
          };
        }
        grouped[userId].total += parseFloat(advance.amount || 0);
        grouped[userId].items.push(advance);
      });

      log('usePayrollAdvances', 'getGroupedByUser', 'Advances grouped', grouped);
      return grouped;
    } catch (err) {
      error.value = err.message;
      log('usePayrollAdvances', 'getGroupedByUser', 'Error grouping advances', err);
      return {};
    }
  };

  return {
    // State
    isLoading,
    error,

    // Methods
    fetchAvailableRecebimentos,
    listAdvancesByPeriod,
    listAdvancesByUserAndPeriod,
    addAdvance,
    addAdvancesBatch,
    deleteAdvance,
    fetchAdvancesSummary,
    getTotalByUserAndPeriod,
    getGroupedByUser
  };
}
